import { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { Plus, ChevronLeft, Ban, RotateCcw } from 'lucide-react';
import { apiGet, apiPost, apiPatch } from '../lib/api';
import { FollowUpList, type FollowUpTask } from '../components/FollowUpList';
import { useToast } from '../contexts/ToastContext';
import { useNow } from '../lib/relativeTime';
import { getLocalToday } from '../lib/dates';
import { TimeSelect } from '../components/TimeSelect';
import { PinButton } from '../components/PinButton';
import { Backlinks } from '../components/Backlinks';
import { ExcerptList } from '../components/excerpts/ExcerptList';
import { listByDestination, type ExcerptView } from '../lib/excerpts';
import { HISTORY_CHANGED_EVENT } from '../lib/history';
import { SeriesSettings, parseSchedulingConfig, type Person } from '../components/series/SeriesSettings';
import { buildMeetingItems, hasWrittenNotes } from '../components/series/PastMeetings';
import { SeriesTabBar, SeriesMeetingList, SeriesItemList, parseSeriesTab, type SeriesTab, type SeriesItems, type SeriesMeeting } from '../components/series/SeriesTabs';

interface MeetingSeries {
  id: string;
  name: string;
  description: string | null;
  cadence: string | null;
  template_content: string | null;
  scheduling_config: string | null;
  end_date: string | null;
  default_attendee_ids?: string[];
  scope_ids?: string[];
}

interface MeetingInstance {
  id: string;
  series_id: string;
  title: string | null;
  meeting_date: string;
  meeting_time: string | null;
  duration_minutes: number | null;
  summary: string | null;
  note_content?: string | null;
  created_at: string;
}

export function MeetingSeriesPage() {
  const { seriesId } = useParams<{ seriesId: string }>();
  const toast = useToast();
  useNow(); // tick every 30s so relative dates stay fresh
  const [series, setSeries] = useState<MeetingSeries | null>(null);
  const [instances, setInstances] = useState<MeetingInstance[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewModal, setShowNewModal] = useState(false);
  const [followUps, setFollowUps] = useState<FollowUpTask[]>([]);
  const [followUpRefreshCount, setFollowUpRefreshCount] = useState(0);
  const [allPeople, setAllPeople] = useState<Person[]>([]);
  // #290: the page is tabbed; the open tab lives in ?tab= so it survives reload.
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = parseSeriesTab(searchParams.get('tab'));
  const setTab = (t: SeriesTab) => setSearchParams(t === 'meetings' ? {} : { tab: t }, { replace: true });
  const [items, setItems] = useState<SeriesItems>({ tasks: [], notes: [] });
  const [excerpts, setExcerpts] = useState<ExcerptView[]>([]);
  const [nextDate, setNextDate] = useState<{ date: string; time: string | null } | null>(null);
  // Bumped once a scheduling change has SAVED, so the next date is asked for after the server has it.
  const [nextDateKey, setNextDateKey] = useState(0);

  useEffect(() => {
    apiGet<Person[]>('/people').then(setAllPeople).catch(console.error);
  }, []);

  async function load() {
    if (!seriesId) return;
    try {
      const [s, i] = await Promise.all([
        apiGet<MeetingSeries>(`/meeting-series/${seriesId}`),
        apiGet<MeetingInstance[]>(`/meeting-series/${seriesId}/instances`),
      ]);
      setSeries({ ...s, default_attendee_ids: s.default_attendee_ids ?? [] });
      setInstances(i);
    } catch (err) {
      console.error('Failed to load meeting series:', err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [seriesId]);

  useEffect(() => {
    if (!seriesId) return;
    apiGet<FollowUpTask[]>(`/meeting-series/${seriesId}/follow-ups`)
      .then(setFollowUps)
      .catch(console.error);
  }, [seriesId, followUpRefreshCount]);

  // Tasks, notes and excerpts for the tabs and their counts; undo/redo can change any of them.
  const loadWork = useCallback(() => {
    if (!seriesId) return;
    // A malformed answer leaves the tabs empty rather than taking the page down.
    apiGet<SeriesItems>(`/meeting-series/${seriesId}/items`)
      .then((r) => setItems({ tasks: Array.isArray(r?.tasks) ? r.tasks : [], notes: Array.isArray(r?.notes) ? r.notes : [] }))
      .catch(console.error);
    listByDestination('meeting_series', seriesId, true)
      .then((r) => setExcerpts(Array.isArray(r) ? r : []))
      .catch(console.error);
  }, [seriesId]);

  useEffect(() => {
    loadWork();
    window.addEventListener(HISTORY_CHANGED_EVENT, loadWork);
    return () => window.removeEventListener(HISTORY_CHANGED_EVENT, loadWork);
  }, [loadWork]);

  useEffect(() => {
    if (!seriesId || !series?.cadence || series.cadence === 'adhoc' || series.end_date) { setNextDate(null); return; }
    // A slower, older answer must not overwrite a newer one.
    let current = true;
    apiGet<{ date: string; time: string | null }>(`/meeting-series/${seriesId}/next-date`)
      .then((r) => { if (current) setNextDate(r?.date ? r : null); })
      .catch(() => { if (current) setNextDate(null); });
    return () => { current = false; };
  }, [seriesId, series?.cadence, series?.end_date, nextDateKey, instances.length]);

  function getSchedulingConfig() {
    return parseSchedulingConfig(series?.scheduling_config ?? null);
  }

  async function handleCreateInstance(date: string, title: string, time: string, duration?: number) {
    if (!seriesId) return;
    try {
      await apiPost(`/meeting-series/${seriesId}/instances`, {
        meeting_date: date,
        title: title || null,
        meeting_time: time || null,
        duration_minutes: duration || null,
      });
      setShowNewModal(false);
      toast.success('Meeting created');
      load();
    } catch (err) {
      console.error('Failed to create meeting:', err);
      toast.error('Failed to create meeting');
    }
  }

  if (loading) {
    return (
      <div className="h-full overflow-auto p-3 sm:p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-accent border-t-transparent" />
        </div>
      </div>
    );
  }

  if (!series) {
    return <div className="h-full overflow-auto p-3 sm:p-6"><div className="text-text-muted text-center py-16">Meeting series not found.</div></div>;
  }

  const attendeeNames = (series.default_attendee_ids ?? [])
    .map((id) => allPeople.find((p) => p.id === id)?.name)
    .filter((n): n is string => !!n);
  // An excerpt that became a task or note is listed in the Tasks or Notes tab, not again under Excerpts.
  const listedIds = new Set([...items.tasks, ...items.notes].map((i) => i.id));
  const inOwnTab = (e: ExcerptView) => e.kind !== 'excerpt' && !!e.linked_entity_id && listedIds.has(e.linked_entity_id);
  const plainExcerpts = excerpts.filter((e) => !inOwnTab(e));
  const itemsByMeeting = buildMeetingItems({ meetingHref: (id) => `/meetings/${series.id}/${id}`, tasks: items.tasks, notes: items.notes, followUps, excerpts: plainExcerpts });
  const withNotes = new Set(instances.filter((i) => hasWrittenNotes(i.note_content, series.template_content)).map((i) => i.id));
  const formatWhen = (inst: SeriesMeeting) => {
    const dur = inst.duration_minutes ?? getSchedulingConfig().default_duration;
    const when = inst.meeting_time ? `${formatTime12(inst.meeting_time)}${dur ? ` - ${formatDuration(dur)}` : ''}` : null;
    return inst.title
      ? { heading: inst.title, sub: [formatDate(inst.meeting_date), when].filter(Boolean).join(' - '), when }
      : { heading: formatDate(inst.meeting_date), sub: when, when };
  };

  return (
    <div className="h-full overflow-auto p-3 sm:p-6">
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <Link to="/meetings" className="flex items-center gap-1 text-sm text-text-muted hover:text-text-primary transition-colors mb-3">
          <ChevronLeft size={16} /> Back to Meetings
        </Link>
        {/* #269: a container, so under 640px the actions take their own row
            and Archive / End Series drop to icons; New Meeting keeps its label. */}
        <div className="@container" data-testid="series-header">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-[1_1_16rem]">
            <h1 className="text-2xl font-bold text-text-primary [overflow-wrap:anywhere]">{series.name}</h1>
            {series.description && (
              <p className="text-sm text-text-muted mt-1">{series.description}</p>
            )}
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-text-muted" data-testid="series-summary">
              {series.cadence && (
                <span className="px-2 py-0.5 text-xs bg-bg-input rounded-full text-text-secondary capitalize">
                  {series.cadence}
                </span>
              )}
              {nextDate && (
                <span>Next: {formatDate(nextDate.date)}{nextDate.time ? ` at ${formatTime12(nextDate.time)}` : ''}</span>
              )}
              {attendeeNames.length > 0 && <span className="[overflow-wrap:anywhere]">{attendeeNames.join(', ')}</span>}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-nowrap @max-[640px]:w-full" data-testid="series-actions">
            <PinButton entityType="meeting_series" entityId={seriesId!} label={series.name} />
            <button
              onClick={() => setShowNewModal(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-accent text-white text-sm font-medium hover:bg-accent-hover transition-colors whitespace-nowrap @max-[640px]:ml-auto"
            >
              <Plus size={16} />
              New Meeting
            </button>
          </div>
        </div>
        </div>
      </div>

      {/* Ended banner */}
      {series.end_date && (
        <div className="mb-6 flex items-center gap-3 px-4 py-3 rounded-lg bg-warning/10 border border-warning/20">
          <Ban size={16} className="text-warning flex-shrink-0" />
          <span className="text-sm text-text-secondary flex-1">
            This series ended on {formatDate(series.end_date)}. No new meetings will be suggested.
          </span>
          <button
            onClick={async () => {
              try {
                const updated = await apiPatch<MeetingSeries>(`/meeting-series/${seriesId}`, { end_date: null });
                setSeries(prev => prev ? { ...prev, end_date: updated.end_date } : prev);
                toast.success('Series reactivated');
              } catch {
                toast.error('Failed to reactivate series');
              }
            }}
            className="flex items-center gap-1 text-xs text-accent hover:text-accent-hover transition-colors flex-shrink-0"
          >
            <RotateCcw size={12} /> Reactivate
          </button>
        </div>
      )}

      <SeriesTabBar
        tab={tab}
        onChange={setTab}
        counts={{
          meetings: instances.length,
          // Open tasks only: the badge says how much is left (#304).
          tasks: items.tasks.filter((t) => t.status !== 'done' && t.status !== 'cancelled').length,
          notes: items.notes.length,
          'follow-ups': followUps.length,
          excerpts: plainExcerpts.length,
        }}
      />

      <div role="tabpanel" id="series-tabpanel" aria-labelledby={`series-tab-${tab}`}>
      {tab === 'settings' && (
        <SeriesSettings
          series={series}
          setSeries={(fn) => setSeries((prev) => prev ? { ...prev, ...fn(prev) } : prev)}
          people={allPeople}
          setPeople={setAllPeople}
          meetings={instances}
          onScheduleSaved={() => setNextDateKey((k) => k + 1)}
          onArchived={() => { window.location.href = '/meetings'; }}
        />
      )}

      {tab === 'meetings' && (
        <SeriesMeetingList
          meetingHref={(id) => `/meetings/${series.id}/${id}`}
          meetings={instances}
          today={getLocalToday()}
          itemsByMeeting={itemsByMeeting}
          withNotes={withNotes}
          formatWhen={formatWhen}
        />
      )}
      {tab === 'tasks' && <SeriesItemList kind="task" items={items.tasks} seriesId={series.id} />}
      {tab === 'notes' && <SeriesItemList kind="note" items={items.notes} seriesId={series.id} />}
      {tab === 'follow-ups' && (
        <FollowUpList
          followUps={followUps}
          showMeetingDate
          onStatusToggle={() => setFollowUpRefreshCount(c => c + 1)}
        />
      )}
      {tab === 'excerpts' && (
        <ExcerptList mode="destination" type="meeting_series" id={series.id} rollup onChanged={loadWork} hide={inOwnTab} />
      )}
      </div>

      {/* Backlinks */}
      <Backlinks entityType="meeting_series" entityId={series.id} className="mt-8" />

      {showNewModal && (
        <NewInstanceModal
          seriesName={series.name}
          onSave={handleCreateInstance}
          onClose={() => setShowNewModal(false)}
        />
      )}
    </div>
    </div>
  );
}

function NewInstanceModal({
  seriesName,
  onSave,
  onClose,
}: {
  seriesName: string;
  onSave: (date: string, title: string, time: string, duration?: number) => void;
  onClose: () => void;
}) {
  const today = getLocalToday();
  const [date, setDate] = useState(today);
  const [title, setTitle] = useState('');
  const [time, setTime] = useState('');
  const [duration, setDuration] = useState('');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div className="bg-bg-surface border border-border rounded-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold text-text-primary mb-1">New Meeting</h2>
        <p className="text-sm text-text-muted mb-4">{seriesName}</p>
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-sm text-text-secondary mb-1">Date *</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full px-3 py-2 bg-bg-input border border-border rounded-md text-text-primary focus:outline-none focus:border-accent text-sm"
              />
            </div>
            <div>
              <label className="block text-sm text-text-secondary mb-1">Time</label>
              <TimeSelect
                value={time}
                onChange={setTime}
                className="w-full px-3 py-2 bg-bg-input border border-border rounded-md text-text-primary focus:outline-none focus:border-accent text-sm"
              />
            </div>
            <div>
              <label className="block text-sm text-text-secondary mb-1">Duration</label>
              <select
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                className="w-full px-3 py-2 bg-bg-input border border-border rounded-md text-text-primary focus:outline-none focus:border-accent text-sm"
              >
                <option value="">Default</option>
                <option value="15">15 min</option>
                <option value="30">30 min</option>
                <option value="45">45 min</option>
                <option value="60">1 hour</option>
                <option value="90">1.5 hours</option>
                <option value="120">2 hours</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm text-text-secondary mb-1">Title (optional)</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={`e.g., ${seriesName} — ${formatDate(date)}`}
              className="w-full px-3 py-2 bg-bg-input border border-border rounded-md text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent text-sm"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 text-sm text-text-secondary hover:text-text-primary transition-colors">
            Cancel
          </button>
          <button
            onClick={() => date && onSave(date, title, time, duration ? Number(duration) : undefined)}
            disabled={!date}
            className="px-4 py-2 text-sm bg-accent text-white rounded-md hover:bg-accent-hover transition-colors disabled:opacity-50"
          >
            Create
          </button>
        </div>
      </div>
    </div>
  );
}

function formatTime12(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return `${hour12}:${String(m).padStart(2, '0')} ${ampm}`;
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr + (dateStr.includes('T') ? '' : 'T00:00:00'));
  return d.toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });
}

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}


